import React, {
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  useForm,
  Controller,
  UseFormReturn,
  FieldValues,
} from "react-hook-form";
import { states } from "../../../../common/select/constants";
import { Input, Select, Button } from "@tyrannosaurustech/ui";
import styled from "styled-components";
import { Theme } from "../../../../components/atoms/theme";
import { ZIP_CODE_PATTERN } from "../../../../utils/regex";

const formatPhoneNumber = (value: string) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  width: 100%;
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const DividerLine = styled.div`
  border-left: 2px solid ${Theme.colors.neutrals_400};
  margin: 0 1.5rem;
`;

const FormCardWrapper = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: ${Theme.shadows.dropshadow_m};
  box-sizing: border-box;
  margin-bottom: 1.5rem;
`;

export interface LawFirmFormProps {
  initialData?: any;
  onFormSubmit: (data: any) => void;
  onFormCancel: (isDirty: boolean, reset: (values?: any) => void) => void;
  submitButtonText?: string;
  isProcessing: boolean;
  disableCancelButton?: boolean;
  renderButtonsExternal?: boolean;
  onFormStateChange?: (isDirty: boolean, isValid: boolean) => void;
}

export interface LawFirmFormRef {
  handleSubmit: UseFormReturn["handleSubmit"];
}

const LawFirmForm = forwardRef<LawFirmFormRef, LawFirmFormProps>(
  (
    {
      initialData,
      onFormSubmit,
      onFormCancel,
      submitButtonText,
      isProcessing,
      disableCancelButton = true,
      renderButtonsExternal = false,
      onFormStateChange,
    },
    ref
  ) => {
    const {
      control,
      register,
      handleSubmit,
      formState,
      reset,
      watch,
      setValue,
    } = useForm<FieldValues>({
      mode: "onChange",
      defaultValues: {
        name: "",
        address: "",
        state: "",
        city: "",
        zip: "",
        contactName: "",
        contactEmail: "",
        phone: "",
      },
    });

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    const { isDirty, isValid } = formState;
    useEffect(() => {
      if (onFormStateChange) {
        onFormStateChange(isDirty, isValid);
      }
    }, [isDirty, isValid, onFormStateChange]);

    const transformedStates = useMemo(() => {
      return [
        { id: "", label: "Select State" },
        ...states.map((s) => ({ id: s.value, label: s.label })),
      ];
    }, []);

    useEffect(() => {
      if (initialData) {
        reset({
          name: initialData.name || "",
          address: initialData.address || "",
          state: initialData.state || "", // Changed from undefined to empty string
          city: initialData.city || "",
          zip: initialData.zip || "",
          contactName: initialData.contactName || "",
          contactEmail: initialData.contactEmail || "",
          phone: initialData.phone || "",
        });
      }
    }, [initialData, reset]);

    const onSubmit = handleSubmit(async (data) => {
      onFormSubmit(data);
    });

    const watchedPhone = watch("phone");

    useEffect(() => {
      const formattedValue = formatPhoneNumber(watchedPhone);
      if (formattedValue !== watchedPhone) {
        setValue("phone", formattedValue, {
          shouldValidate: true,
        });
      }
    }, [watchedPhone, setValue]);

    return (
      <FormCardWrapper>
        <form onSubmit={onSubmit}>
          <FormContainer>
            <FormColumn>
              <Input
                label="Firm Name"
                placeholder="Firm Name"
                required
                className="w-full"
                {...register("name", { required: true })}
              />
              <div style={{ display: "flex", gap: "1.5rem" }}>
                <Input
                  label="Address"
                  placeholder="Address"
                  required
                  className="w-full flex-1"
                  {...register("address", { required: true })}
                />
                <Input
                  label="City"
                  placeholder="City"
                  required
                  className="w-full flex-1"
                  {...register("city", { required: true })}
                />
              </div>
              <div style={{ display: "flex", gap: "1.5rem" }}>
                <div className="w-full flex-1">
                  <Controller
                    name="state"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        label="State"
                        value={
                          transformedStates.find(
                            (option) => option.id === value
                          ) || transformedStates[0]
                        }
                        onChange={(selectedOption) =>
                          onChange(
                            selectedOption?.id === "" ? "" : selectedOption?.id
                          )
                        }
                        options={transformedStates}
                        className="w-full"
                        required
                      />
                    )}
                  />
                </div>
                <div className="w-full flex-1">
                  <Input
                    label="Zip Code"
                    placeholder="Zip Code"
                    required
                    className="w-full"
                    {...register("zip", {
                      required: true,
                      pattern: {
                        value: ZIP_CODE_PATTERN,
                        message: "Enter a valid zip code",
                      },
                    })}
                  />
                </div>
              </div>
            </FormColumn>
            <DividerLine />
            <FormColumn>
              <Input
                label="Contact Name"
                placeholder="Contact Name"
                required
                className="w-full"
                {...register("contactName", { required: true })}
              />
              <Input
                label="Email"
                placeholder="Email"
                required
                className="w-full"
                {...register("contactEmail", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
              />
              <Input
                label="Phone Number"
                placeholder="Phone Number"
                className="w-full"
                {...register("phone", {
                  required: false,
                  pattern: {
                    value: /^\(\d{3}\) \d{3}-\d{4}$/,
                    message:
                      "Invalid phone number format (e.g., (123) 456-7890)",
                  },
                })}
              />
            </FormColumn>
          </FormContainer>
        </form>
        {!renderButtonsExternal && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "1.5rem",
            }}
          >
            <Button
              type="button"
              base="secondary"
              onClick={() => onFormCancel(formState.isDirty, reset)}
              label="Cancel"
              disabled={disableCancelButton && !formState.isDirty}
            />
            <div style={{ marginLeft: "1rem" }} />
            <Button
              type="button"
              onClick={onSubmit}
              label={submitButtonText}
              loading={isProcessing}
              disabled={!formState.isDirty || !formState.isValid}
            />
          </div>
        )}
      </FormCardWrapper>
    );
  }
);

export default LawFirmForm;
